<template>
  <span
    ref="anchorRef"
    class="inline-flex items-center"
    @mouseenter="onEnter"
    @mouseleave="onLeave"
    @focusin="onEnter"
    @focusout="onLeave"
    @click="onClick"
  >
    <slot />

    <Teleport v-if="mounted" to="body">
      <Transition name="tt">
        <div
          v-if="open"
          ref="tipRef"
          class="fixed z-[1000] pointer-events-none"
          :style="positionStyle"
          role="tooltip"
        >
          <div
            class="pointer-events-auto rounded-xl shadow-float p-3.5 text-xs leading-relaxed ring-1 ring-ink-800 bg-ink-900 text-white"
            :style="{ maxWidth: `${maxWidth}px` }"
          >
            <div v-if="title" class="flex items-center gap-2 mb-1">
              <span v-if="icon" class="text-brand-500">
                <i :class="icon" />
              </span>
              <span class="font-semibold text-white text-sm">{{ title }}</span>
            </div>
            <div v-if="body || subtitle" class="text-ink-300 text-[11px] mb-2">
              {{ subtitle || body }}
            </div>
            <slot name="body" />

            <!-- Metric row -->
            <div v-if="metrics && metrics.length" class="flex gap-4 mt-2 pt-2 border-t border-ink-700">
              <div v-for="(m, i) in metrics" :key="i" class="text-center min-w-[40px]">
                <span class="block font-bold text-sm text-white">{{ m.value }}</span>
                <span class="block text-[10px] text-ink-500 uppercase tracking-wider">{{ m.label }}</span>
              </div>
            </div>

            <a
              v-if="linkText && linkHref"
              :href="linkHref"
              class="mt-2 inline-flex items-center gap-1 text-brand-500 hover:text-brand-200 font-medium"
              @click.stop
            >
              {{ linkText }}
              <svg class="w-3 h-3" viewBox="0 0 20 20" fill="currentColor"><path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z"/></svg>
            </a>
            <div
              class="absolute w-2 h-2 bg-ink-900 rotate-45 ring-1 ring-ink-800"
              :style="arrowStyle"
            ></div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </span>
</template>

<script setup>
import { ref, nextTick, onMounted, onBeforeUnmount } from 'vue';

const props = defineProps({
  title:     { type: String, default: '' },
  body:      { type: String, default: '' },
  subtitle:  { type: String, default: '' },
  icon:      { type: String, default: '' },
  linkText:  { type: String, default: '' },
  linkHref:  { type: String, default: '' },
  placement: { type: String, default: 'auto' }, // auto | top | bottom | left | right
  delay:     { type: Number, default: 120 },
  maxWidth:  { type: Number, default: 280 },
  metrics:   { type: Array, default: null },     // [{ value, label }]
});

const anchorRef = ref(null);
const tipRef = ref(null);
const open = ref(false);
// Client-only: keeps this <Teleport> out of the SSR snapshot and the first
// hydration render so the two agree exactly (Vue's client teleport hydration
// can't reconcile an un-opened teleported tooltip against the SSR anchors,
// which produced a spurious "Hydration node mismatch ... expected: Comment"
// on pages that render many tooltips, e.g. /pricing). onMounted never runs
// during SSR; tooltips only ever appear on hover, i.e. after mount anyway.
const mounted = ref(false);
onMounted(() => {
  mounted.value = true;
});
const positionStyle = ref({ top: '0px', left: '0px' });
const arrowStyle = ref({});
let openTimer = null;
let closeTimer = null;
let isTouch = false;

const place = async () => {
  await nextTick();
  if (!anchorRef.value || !tipRef.value) return;
  const a = anchorRef.value.getBoundingClientRect();
  const t = tipRef.value.firstElementChild.getBoundingClientRect();
  const gap = 10;
  let top = 0, left = 0, placement = props.placement;
  let arrowTop = '', arrowLeft = '';

  // Auto-placement: pick direction with most space
  if (placement === 'auto') {
    const spaceTop = a.top;
    const spaceBottom = window.innerHeight - a.bottom;
    const spaceLeft = a.right;
    const spaceRight = window.innerWidth - a.left;

    if (spaceBottom >= t.height + gap) placement = 'bottom';
    else if (spaceTop >= t.height + gap) placement = 'top';
    else if (spaceRight >= t.width + gap) placement = 'right';
    else placement = 'left';
  }

  switch (placement) {
    case 'bottom':
      top = a.bottom + gap;
      left = a.left + a.width / 2 - t.width / 2;
      arrowTop = '-4px'; arrowLeft = `${t.width / 2 - 4}px`;
      break;
    case 'left':
      top = a.top + a.height / 2 - t.height / 2;
      left = a.left - t.width - gap;
      arrowTop = `${t.height / 2 - 4}px`; arrowLeft = `${t.width - 4}px`;
      break;
    case 'right':
      top = a.top + a.height / 2 - t.height / 2;
      left = a.right + gap;
      arrowTop = `${t.height / 2 - 4}px`; arrowLeft = '-4px';
      break;
    case 'top':
    default:
      top = a.top - t.height - gap;
      left = a.left + a.width / 2 - t.width / 2;
      arrowTop = `${t.height - 4}px`; arrowLeft = `${t.width / 2 - 4}px`;
  }

  // Clamp to viewport
  const pad = 8;
  left = Math.max(pad, Math.min(left, window.innerWidth - t.width - pad));
  top  = Math.max(pad, Math.min(top,  window.innerHeight - t.height - pad));

  positionStyle.value = { top: `${top}px`, left: `${left}px` };
  arrowStyle.value = { top: arrowTop, left: arrowLeft };
};

const show = () => {
  clearTimeout(closeTimer);
  openTimer = setTimeout(async () => {
    open.value = true;
    await place();
  }, props.delay);
};

const hide = () => {
  clearTimeout(openTimer);
  closeTimer = setTimeout(() => { open.value = false; }, 80);
};

const onEnter = () => { if (!isTouch) show(); };
const onLeave = () => { if (!isTouch) hide(); };
const onClick = async (e) => {
  if (window.matchMedia('(hover: none)').matches) {
    isTouch = true;
    open.value = !open.value;
    if (open.value) await place();
  }
};

const onScroll = () => { if (open.value) place(); };
window.addEventListener('scroll', onScroll, true);
window.addEventListener('resize', onScroll);

onBeforeUnmount(() => {
  clearTimeout(openTimer); clearTimeout(closeTimer);
  window.removeEventListener('scroll', onScroll, true);
  window.removeEventListener('resize', onScroll);
});
</script>

<style scoped>
.tt-enter-active, .tt-leave-active { transition: opacity 140ms ease, transform 140ms ease; }
.tt-enter-from, .tt-leave-to { opacity: 0; transform: translateY(2px); }
</style>
